import type { FormEvent, JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DUREE_LIEN_DEPOT_JOURS, PLAFOND_DEPOT_DEFAUT_OCTETS } from '@artosera/shared';
import { Badge, Button, Dialog, Input, useToast, type BadgeTone } from '@artosera/ui';
import {
  creerLien,
  listerLiens,
  octetsEnGio,
  revoquerLien,
  type LienVue,
} from '../../../lib/depots.js';
import { formaterDate, formaterOctets } from '../../../lib/format.js';
import formStyles from '../../../styles/form.module.css';
import pageStyles from '../../../styles/page.module.css';
import { cleDepots } from './DepotsBloc.js';
import { messageReprise } from './messages.js';
import styles from './Reprise.module.css';

const TON_LIEN: Record<LienVue['etat'], BadgeTone> = {
  actif: 'available',
  expire: 'neutral',
  revoque: 'withdrawn',
};

const PLAFOND_DEFAUT_GIO = PLAFOND_DEPOT_DEFAUT_OCTETS / 1024 ** 3;

const cleLiens = (idGalerie: string): readonly string[] => [
  'console',
  'galerie',
  idGalerie,
  'reprise',
  'liens-depot',
];

/**
 * Fiche galerie › Reprise › Liens de dépôt (A5, ARTO-129) : une page d'envoi
 * sans compte pour la galerie. Son adresse n'est montrée qu'une fois, à la
 * création — l'API n'en garde que l'empreinte ; un lien se révoque d'un geste.
 */
export function LiensDepotBloc({ idGalerie }: { idGalerie: string }): JSX.Element {
  const { t } = useTranslation('console');
  const client = useQueryClient();
  const { toast } = useToast();
  const [formulaire, setFormulaire] = useState(false);
  const [libelle, setLibelle] = useState('');
  const [plafond, setPlafond] = useState(String(PLAFOND_DEFAUT_GIO));
  const [duree, setDuree] = useState(String(DUREE_LIEN_DEPOT_JOURS));
  const [adresse, setAdresse] = useState<string | null>(null);
  const [aRevoquer, setARevoquer] = useState<LienVue | null>(null);

  const liens = useQuery({ queryKey: cleLiens(idGalerie), queryFn: () => listerLiens(idGalerie) });
  const rafraichir = () => {
    void client.invalidateQueries({ queryKey: cleLiens(idGalerie) });
    void client.invalidateQueries({ queryKey: cleDepots(idGalerie) });
  };

  const creer = useMutation({
    mutationFn: () =>
      creerLien(idGalerie, {
        libelle: libelle.trim(),
        octetsMax: octetsEnGio(Number(plafond) || PLAFOND_DEFAUT_GIO),
        dureeJours: Math.min(
          DUREE_LIEN_DEPOT_JOURS,
          Math.max(1, Math.round(Number(duree) || DUREE_LIEN_DEPOT_JOURS)),
        ),
      }),
    onSuccess: ({ url }) => {
      setAdresse(url);
      setFormulaire(false);
      setLibelle('');
      rafraichir();
    },
  });

  const revoquer = useMutation({
    mutationFn: (lien: LienVue) => revoquerLien(idGalerie, lien.id),
    onSuccess: () => {
      setARevoquer(null);
      toast({ title: t('reprise.liens.revoque'), closeLabel: t('fiche.domaines.fermer') });
      rafraichir();
    },
  });

  const copier = async (url: string) => {
    await navigator.clipboard.writeText(url);
    toast({ title: t('reprise.liens.copie'), closeLabel: t('fiche.domaines.fermer') });
  };

  const soumettre = (evenement: FormEvent) => {
    evenement.preventDefault();
    creer.mutate();
  };

  return (
    <section className={pageStyles.panneau} aria-labelledby="reprise-liens">
      <div className={pageStyles.sectionTitre}>
        <h2 id="reprise-liens">{t('reprise.liens.titre')}</h2>
        {!formulaire ? (
          <Button
            onClick={() => {
              setAdresse(null);
              setFormulaire(true);
            }}
          >
            {t('reprise.liens.nouveau')}
          </Button>
        ) : null}
      </div>
      <div className={styles.pile}>
        <p className={styles.texte}>{t('reprise.liens.texte')}</p>

        {formulaire ? (
          <form className={styles.carte} onSubmit={soumettre}>
            <div className={formStyles.grille}>
              <div className={`${formStyles.champ} ${formStyles.pleineLargeur}`}>
                <label htmlFor="lien-libelle">{t('reprise.liens.libelle')}</label>
                <Input
                  id="lien-libelle"
                  value={libelle}
                  maxLength={200}
                  onChange={(evenement) => setLibelle(evenement.target.value)}
                />
              </div>
              <div className={formStyles.champ}>
                <label htmlFor="lien-plafond">{t('reprise.liens.plafond')}</label>
                <Input
                  id="lien-plafond"
                  type="number"
                  inputMode="decimal"
                  min={0.001}
                  max={200}
                  step="any"
                  value={plafond}
                  onChange={(evenement) => setPlafond(evenement.target.value)}
                />
              </div>
              <div className={formStyles.champ}>
                <label htmlFor="lien-duree">{t('reprise.liens.duree')}</label>
                <Input
                  id="lien-duree"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={DUREE_LIEN_DEPOT_JOURS}
                  value={duree}
                  onChange={(evenement) => setDuree(evenement.target.value)}
                />
              </div>
            </div>
            {creer.isError ? (
              <p className={styles.erreur} role="alert">
                {messageReprise(t, creer.error)}
              </p>
            ) : null}
            <div className={styles.actions}>
              <Button type="submit" loading={creer.isPending}>
                {t('reprise.liens.creer')}
              </Button>
              <Button variant="ghost" onClick={() => setFormulaire(false)}>
                {t('reprise.liens.annuler')}
              </Button>
            </div>
          </form>
        ) : null}

        {adresse !== null ? (
          <div className={styles.carte} role="status">
            <p className={styles.texte}>{t('reprise.liens.cree')}</p>
            <div className={formStyles.champ}>
              <label htmlFor="lien-adresse">{t('reprise.liens.adresse')}</label>
              <Input
                id="lien-adresse"
                readOnly
                value={adresse}
                onFocus={(evenement) => evenement.currentTarget.select()}
              />
            </div>
            <div className={styles.actions}>
              <Button onClick={() => void copier(adresse)}>{t('reprise.liens.copier')}</Button>
            </div>
          </div>
        ) : null}

        {liens.isLoading ? <p className={pageStyles.videEtat}>{t('reprise.chargement')}</p> : null}
        {liens.isError ? (
          <p className={styles.erreur} role="alert">
            {t('reprise.liens.erreur')}
          </p>
        ) : null}
        {liens.isSuccess && liens.data.length === 0 ? (
          <p className={pageStyles.videEtat}>{t('reprise.liens.aucun')}</p>
        ) : null}
        {liens.isSuccess && liens.data.length > 0 ? (
          <ul className={styles.cartes}>
            {liens.data.map((lien) => {
              const titre = lien.libelle || t('reprise.liens.sansNom');
              return (
                <li key={lien.id} className={styles.carte} aria-label={titre}>
                  <div className={styles.rangee}>
                    <h3 className={styles.sousTitre}>{titre}</h3>
                    <Badge tone={TON_LIEN[lien.etat]}>
                      {t(`reprise.liens.etats.${lien.etat}`)}
                    </Badge>
                  </div>
                  <p className={styles.discret}>
                    {[
                      t('reprise.liens.echeance', { date: formaterDate(lien.expireLe, '—') }),
                      t('reprise.liens.volume', {
                        recus: formaterOctets(lien.octetsRecus),
                        plafond: formaterOctets(lien.octetsMax),
                      }),
                      t('reprise.depots.fichiers', { count: lien.fichiers }),
                      t('reprise.liens.auteur', { email: lien.creePar }),
                    ].join(' · ')}
                  </p>
                  {lien.etat === 'actif' ? (
                    <div className={styles.actions}>
                      <Button variant="ghost" onClick={() => setARevoquer(lien)}>
                        {t('reprise.liens.revoquer')}
                      </Button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>

      <Dialog
        open={aRevoquer !== null}
        onOpenChange={(ouvert) => {
          if (!ouvert) setARevoquer(null);
        }}
        title={t('reprise.liens.revoquerTitre')}
        description={t('reprise.liens.revoquerTexte')}
        closeLabel={t('reprise.liens.annuler')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setARevoquer(null)}>
              {t('reprise.liens.annuler')}
            </Button>
            <Button
              variant="danger"
              loading={revoquer.isPending}
              onClick={() => {
                if (aRevoquer) revoquer.mutate(aRevoquer);
              }}
            >
              {t('reprise.liens.revoquerConfirmer')}
            </Button>
          </>
        }
      >
        {revoquer.isError ? (
          <p className={styles.erreur} role="alert">
            {messageReprise(t, revoquer.error)}
          </p>
        ) : null}
      </Dialog>
    </section>
  );
}
